import { Skeleton } from "@/components/ui/skeleton";

/**
 * Generic full-page loading skeleton used as the default Suspense fallback for
 * lazy-loaded routes. Mimics a typical page layout (hero header + content
 * blocks) so route transitions feel instant instead of showing a black screen.
 */
const PageSkeleton = () => {
  return (
    <div
      className="min-h-screen bg-background text-foreground"
      role="status"
      aria-label="Loading page"
    >
      {/* Top nav placeholder */}
      <div className="border-b border-border/40 px-4 py-3">
        <div className="container flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <div className="hidden md:flex items-center gap-3">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        </div>
      </div>

      <div className="container py-8 md:py-12 space-y-6">
        {/* Hero block */}
        <div className="space-y-3">
          <Skeleton className="h-8 w-2/3 max-w-md" />
          <Skeleton className="h-4 w-full max-w-xl" />
          <Skeleton className="h-4 w-3/4 max-w-lg" />
        </div>

        {/* Content grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-border/40 bg-card/40 p-4 space-y-3"
            >
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-8 w-24 rounded-md mt-2" />
            </div>
          ))}
        </div>
      </div>

      <span className="sr-only">Loading content…</span>
    </div>
  );
};

/**
 * Smaller in-page skeleton used inside route components when lazy-loading a
 * sub-section (e.g. the Leaderboard panel inside the /leaderboard page).
 */
export const SectionSkeleton = ({ rows = 6 }: { rows?: number }) => (
  <div className="space-y-3" role="status" aria-label="Loading section">
    <Skeleton className="h-10 w-full" />
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full" />
    ))}
    <span className="sr-only">Loading…</span>
  </div>
);

export default PageSkeleton;
